import { useEffect, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, BadgeCheck, X, ArrowLeft, Loader2, Sparkles, CheckCircle2, AlertTriangle, MinusCircle, LifeBuoy, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import HealthPill from "@/components/app/HealthPill";
import { useTranslation } from "react-i18next";

export default function AdminVerifications() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const TAB_LABELS = {
    pending: t("admin.pending"),
    approved: t("admin.approved"),
    rejected: t("admin.rejected"),
  };

  const TYPE_LABELS = {
    passport: t("admin.docTypes.passport"),
    id_card: t("admin.docTypes.id_card"),
    drivers_license: t("admin.docTypes.drivers_license"),
  };

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get(`/admin/verifications?status=${tab}`);
      setItems(data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
      else toast.error(fmtError(e.response?.data?.detail) || t("errors.generic"));
      setItems([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const decide = async (action) => {
    if (!reviewing) return;
    if (action === "reject" && !rejectReason.trim()) {
      toast.error(t("admin.rejectReasonRequired"));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/verifications/${reviewing.submission.id}/decide`, {
        action,
        rejection_reason: action === "reject" ? rejectReason.trim() : null,
      });
      toast.success(action === "approve" ? t("admin.approvedToast") : t("admin.rejectedToast"));
      setReviewing(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || t("errors.generic"));
    }
    finally { setBusy(false); }
  };

  if (forbidden) {
    return (
      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-12 pb-32 text-center">
        <h1 className="font-display font-black text-2xl">{t("admin.adminsOnly")}</h1>
        <p className="text-[#4A4F58] mt-2">{t("admin.noAccess")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-32">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => nav("/admin")} className="btn-nb p-2.5 sm:p-3" data-testid="admin-back">
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5}/>
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-display font-black text-2xl sm:text-4xl tracking-tight leading-none">{t("admin.title")}</h1>
              <HealthPill />
            </div>
            <p className="text-xs sm:text-sm text-[#4A4F58] mt-0.5">{t("admin.subtitle")}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full sm:w-80">
          <Link to="/admin/tickets" className="btn-nb !px-3 !py-2 text-xs sm:text-sm font-bold justify-center text-center whitespace-nowrap">
            <LifeBuoy className="w-4 h-4 me-1.5 inline" /> Support Tickets
          </Link>
          <Link to="/admin/accounts" className="btn-nb btn-nb-dark !px-3 !py-2 text-xs sm:text-sm font-bold justify-center text-center whitespace-nowrap">
            <Users className="w-4 h-4 me-1.5 inline" /> Members
          </Link>
        </div>
      </div>

      <div className="mt-6 flex gap-2 glass-strong rounded-full p-1.5 shadow-soft">
        {["pending", "approved", "rejected"].map((tk) => (
          <button key={tk} onClick={() => setTab(tk)} data-testid={`tab-${tk}`}
            className={`flex-1 py-2 rounded-full font-display font-bold text-sm transition-all ${tab === tk ? "bg-[#0F1115] text-white shadow-soft" : "text-[#4A4F58] hover:text-[#0F1115]"}`}>
            {TAB_LABELS[tk]}
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3" data-testid="admin-list">
        {items === null ? (
          <div className="card-nb p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"/></div>
        ) : items.length === 0 ? (
          <div className="card-nb p-8 text-center text-[#4A4F58]">{t("admin.noSubmissions", { tab: TAB_LABELS[tab] })}</div>
        ) : (
          items.map((it) => (
            <button key={it.user.id} onClick={() => { setReviewing(it); setRejectReason(""); }} data-testid={`review-${it.user.id}`}
              className="card-nb w-full p-4 text-left hover:-translate-y-0.5 transition-all flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-white border border-[#0A0B0D]/10 flex items-center justify-center font-display font-black overflow-hidden shrink-0">
                {it.user.avatar_url ? <img src={it.user.avatar_url} alt="" className="w-full h-full object-cover"/> : it.user.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-extrabold truncate">{it.user.name}</span>
                  {it.user.verified && <BadgeCheck className="w-4 h-4 text-[#14C8B0]" strokeWidth={3} fill="#DDF5EA"/>}
                </div>
                <p className="text-xs text-[#4A4F58] mt-0.5 truncate">
                  {TYPE_LABELS[it.submission.document_type] || it.submission.document_type}
                  {" · "}{it.submission.full_name}
                  {" · "}{t("profile.dob")} {it.submission.date_of_birth}
                </p>
                <p className="text-[11px] text-[#7C828D] mt-0.5">
                  {t("admin.submittedOn", { date: new Date(it.submission.submitted_at).toLocaleString() })}
                </p>
              </div>
              <span className={`tag-nb ${it.submission.status === "pending" ? "bg-[#FFF1D6]" : it.submission.status === "approved" ? "bg-[#DDF5EA]" : "bg-[#FFE6E0]"}`}>
                {TAB_LABELS[it.submission.status] || it.submission.status}
              </span>
            </button>
          ))
        )}
      </div>

      {reviewing && (
        <div className="fixed inset-0 bg-[#0A0B0D]/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4" onClick={() => setReviewing(null)}>
          <div className="card-nb p-6 max-w-lg w-full bg-white animate-pop max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-display font-black text-xl">{reviewing.user.name}</h3>
                <p className="text-xs text-[#7C828D]">{reviewing.user.email}</p>
              </div>
              <button onClick={() => setReviewing(null)} className="btn-nb" data-testid="modal-close"><X className="w-4 h-4" strokeWidth={2.5}/></button>
            </div>

            <div className="rounded-2xl overflow-hidden border border-[#0A0B0D]/10 mb-4 bg-[#F4F2ED]" data-testid="review-doc">
              <img src={reviewing.submission.document_url} alt={t("profile.idDocAlt")} className="w-full max-h-96 object-contain"/>
            </div>

            <AiVerdictCard
              ai={reviewing.submission.ai}
              claimedName={reviewing.submission.full_name}
              claimedDob={reviewing.submission.date_of_birth}
              t={t}
            />

            <dl className="text-sm space-y-1.5 mb-4 mt-4">
              <Row k={t("admin.docType")} v={TYPE_LABELS[reviewing.submission.document_type] || reviewing.submission.document_type}/>
              <Row k={t("admin.fullName")} v={reviewing.submission.full_name}/>
              <Row k={t("admin.dob")} v={reviewing.submission.date_of_birth}/>
              <Row k={t("admin.ageAtSub")} v={reviewing.submission.age_at_submission}/>
              <Row k={t("admin.accountName")} v={reviewing.user.name}/>
              <Row k={t("admin.accountAge")} v={reviewing.user.age}/>
            </dl>

            {reviewing.submission.status === "pending" ? (
              <>
                <button onClick={() => approve(reviewing.user.id)} disabled={busy} className="btn-nb btn-nb-primary w-full mb-2" data-testid="approve-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <BadgeCheck className="w-4 h-4" strokeWidth={2.5}/>} {t("admin.approve")}
                </button>
                <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={t("admin.reasonPh")} className="input-nb min-h-[60px] mb-2" data-testid="reject-reason"/>
                <button onClick={() => reject(reviewing.user.id)} disabled={busy} className="btn-nb btn-nb-coral w-full" data-testid="reject-btn">
                  {t("admin.reject")}
                </button>
              </>
            ) : (
              <div className={`p-3 rounded-2xl text-sm font-display font-semibold ${reviewing.submission.status === "approved" ? "bg-[#DDF5EA] text-[#0F9D8F]" : "bg-[#FFE6E0] text-[#D14848]"}`}>
                {reviewing.submission.status === "approved"
                  ? t("admin.approvedOn", { date: new Date(reviewing.submission.reviewed_at).toLocaleString() })
                  : t("admin.rejectedReason", { reason: reviewing.submission.reject_reason })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-[#7C828D] uppercase tracking-wide text-[11px] font-semibold">{k}</dt>
      <dd className="font-display font-semibold text-right break-words">{v}</dd>
    </div>
  );
}

/**
 * AI-augmented review card. Shows Gemini's recommendation, confidence score,
 * extracted fields side-by-side with what the user claimed, plus the reasons
 * the model gave. Lets admins resolve borderline cases in seconds.
 */
function AiVerdictCard({ ai, claimedName, claimedDob, t }) {
  if (!ai) return null;
  // While the background AI task is still running, show a friendly placeholder.
  if (ai.status === "pending") {
    return (
      <div className="rounded-2xl border-2 border-[#E2DFD4] bg-[#FAF8F2] p-4 flex items-center gap-3" data-testid="ai-verdict-pending">
        <Loader2 className="w-5 h-5 animate-spin text-[#7C828D]" />
        <div>
          <div className="font-display font-bold text-sm">{t("admin.aiAnalyzing")}</div>
          <div className="text-xs text-[#7C828D]">{t("admin.aiAnalyzingHint")}</div>
        </div>
      </div>
    );
  }
  if (ai.status === "error" || ai.error) {
    return (
      <div className="rounded-2xl border-2 border-[#FFE6E0] bg-[#FFF6F4] p-4" data-testid="ai-verdict-error">
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-4 h-4 text-[#D14848]" strokeWidth={3} />
          <span className="font-display font-bold text-sm text-[#D14848]">{t("admin.aiFailed")}</span>
        </div>
        <p className="text-xs text-[#7C828D]">{(ai.reasons && ai.reasons[0]) || ai.error || "—"}</p>
      </div>
    );
  }

  // Map decision to colour scheme + icon + label.
  const decision = ai.decision || "review";
  const confidence = typeof ai.confidence === "number" ? ai.confidence : 0;
  const styles = {
    approved: { bg: "bg-[#DDF5EA]", border: "border-[#14C8B0]", text: "text-[#0F9D8F]", Icon: CheckCircle2, label: t("admin.aiRecApprove") },
    rejected: { bg: "bg-[#FFE6E0]", border: "border-[#FF8A75]", text: "text-[#D14848]", Icon: MinusCircle, label: t("admin.aiRecReject") },
    review:   { bg: "bg-[#FFF6E0]", border: "border-[#FFC93C]", text: "text-[#8B6914]", Icon: AlertTriangle, label: t("admin.aiRecReview") },
  }[decision] || { bg: "bg-[#F4F2ED]", border: "border-[#E2DFD4]", text: "text-[#0A0B0D]", Icon: Sparkles, label: decision };
  const { Icon } = styles;

  const ex = ai.extracted || {};
  const checks = ai.checks || {};

  return (
    <div className={`rounded-2xl border-2 ${styles.border} ${styles.bg} p-4`} data-testid="ai-verdict-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-white border-2 border-current flex items-center justify-center shrink-0">
          <Icon className={`w-4 h-4 ${styles.text}`} strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-display font-black text-sm ${styles.text} uppercase tracking-wide`}>
            <Sparkles className="w-3.5 h-3.5 inline mr-1 -mt-0.5" strokeWidth={3} />
            {t("admin.aiRecommendation")} {styles.label}
          </div>
          <div className="text-xs text-[#7C828D]">{t("admin.aiConfidence")} <span className="font-bold">{confidence}%</span> · {ai.model || "gemini"}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <AiCmpRow label={t("admin.fullName")} claimed={claimedName} extracted={ex.full_name} match={checks.name_match} tClaimed={t("admin.aiClaimed")} />
        <AiCmpRow label={t("admin.dob")} claimed={claimedDob} extracted={ex.date_of_birth} match={checks.dob_match} tClaimed={t("admin.aiClaimed")} />
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-[#7C828D] font-bold">{t("admin.docType")}</span>
          <span className="text-xs font-semibold">{ex.document_type || "—"}</span>
          <span className="text-[10px] text-[#7C828D]">{ex.country ? `· ${ex.country}` : ""}</span>
        </div>
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-[#7C828D] font-bold">{t("admin.aiChecks")}</span>
          <div className="flex flex-wrap gap-1 text-[10px]">
            <Pill ok={checks.is_id_document} label={t("admin.aiCheckIsId")} />
            <Pill ok={checks.looks_authentic} label={t("admin.aiCheckAuthentic")} />
            <Pill ok={checks.age_18_plus} label="18+" />
          </div>
        </div>
      </div>

      {Array.isArray(ai.reasons) && ai.reasons.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-[#7C828D] font-semibold hover:text-[#0A0B0D]">{t("admin.aiReasons")}</summary>
          <ul className="mt-2 space-y-1 list-disc list-inside text-[#3D424B]">
            {ai.reasons.slice(0, 5).map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </details>
      )}
    </div>
  );
}

function AiCmpRow({ label, claimed, extracted, match, tClaimed }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wide text-[#7C828D] font-bold">{label}</span>
      <div className="flex items-center gap-1.5 text-xs">
        <span className="font-semibold truncate">{extracted || "—"}</span>
        {match === true && <CheckCircle2 className="w-3.5 h-3.5 text-[#0F9D8F] shrink-0" strokeWidth={3} />}
        {match === false && <X className="w-3.5 h-3.5 text-[#D14848] shrink-0" strokeWidth={3} />}
      </div>
      <span className="text-[10px] text-[#7C828D]">{tClaimed} {claimed || "—"}</span>
    </div>
  );
}

function Pill({ ok, label }) {
  const color = ok === true ? "bg-[#DDF5EA] text-[#0F9D8F]"
              : ok === false ? "bg-[#FFE6E0] text-[#D14848]"
              : "bg-[#F4F2ED] text-[#7C828D]";
  return <span className={`${color} px-1.5 py-0.5 rounded font-bold`}>{label}</span>;
}
